import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { bannersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Image, Upload, Trash2, Loader2, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';

const PAGE_TYPES = [
  { value: 'recharge', label: 'Recharge (Prepaid/Postpaid)' },
  { value: 'dth', label: 'DTH Recharge' },
  { value: 'fastag', label: 'FASTag' },
  { value: 'electricity', label: 'Electricity Bill' },
  { value: 'loan', label: 'Loan Payment' },
  { value: 'gas', label: 'Gas Bill' },
  { value: 'dashboard', label: 'Dashboard' },
];

export default function BannerManager() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('');
  const [uploadPage, setUploadPage] = useState('recharge');
  const fileRef = useRef(null);

  useEffect(() => { fetchBanners(); }, [filter]);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const r = await bannersAPI.list(filter);
      setBanners(r.data?.banners || []);
    } catch {} finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Only PNG, JPG, WEBP allowed'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5MB allowed'); return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('page_type', uploadPage);
      await bannersAPI.upload(fd);
      toast.success('Banner uploaded!');
      fetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await bannersAPI.delete(id);
      toast.success('Deleted');
      fetchBanners();
    } catch { toast.error('Delete failed'); }
  };

  const handleToggle = async (banner) => {
    try {
      await bannersAPI.update(banner.id, { status: banner.status === 'active' ? 'inactive' : 'active' });
      fetchBanners();
    } catch { toast.error('Update failed'); }
  };

  const grouped = PAGE_TYPES.reduce((acc, pt) => {
    acc[pt.value] = banners.filter(b => b.page_type === pt.value);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="banner-manager-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Image className="w-6 h-6 text-[#4680ff]" />
              Banner Manager
            </h1>
            <p className="text-sm text-slate-500">Upload and manage banners for each page</p>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-sm font-bold text-slate-700 mb-4">Upload New Banner</h3>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Page</label>
              <Select value={uploadPage} onValueChange={setUploadPage}>
                <SelectTrigger className="h-10" data-testid="upload-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_TYPES.map(pt => (
                    <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Image (PNG, JPG, WEBP — max 5MB)</label>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleUpload}
                className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-[#4680ff] hover:file:bg-blue-100 cursor-pointer"
                data-testid="banner-file-input"
              />
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-sm text-[#4680ff]">
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
              </div>
            )}
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter('')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${!filter ? 'bg-[#4680ff] text-white border-[#4680ff]' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
            data-testid="filter-all"
          >All</button>
          {PAGE_TYPES.map(pt => (
            <button
              key={pt.value}
              onClick={() => setFilter(pt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${filter === pt.value ? 'bg-[#4680ff] text-white border-[#4680ff]' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
              data-testid={`filter-${pt.value}`}
            >{pt.label}</button>
          ))}
        </div>

        {/* Banners Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-48 rounded-2xl bg-slate-100 animate-pulse" />)}
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
            <Image className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No banners yet. Upload one above.</p>
          </div>
        ) : (
          Object.entries(grouped).filter(([, items]) => items.length > 0).map(([pageType, items]) => {
            const label = PAGE_TYPES.find(p => p.value === pageType)?.label || pageType;
            return (
              <div key={pageType} className="space-y-3">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">{label}</Badge>
                  <span className="text-xs text-slate-400">{items.length} banner{items.length > 1 ? 's' : ''}</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map(banner => (
                    <div key={banner.id} className={`relative group rounded-2xl border overflow-hidden transition-all ${banner.status === 'active' ? 'border-slate-200 shadow-sm' : 'border-red-200 opacity-60'}`}>
                      <img
                        src={banner.image_url}
                        alt={banner.image_name}
                        className="w-full h-40 object-cover"
                        data-testid={`banner-img-${banner.id}`}
                      />
                      {/* Overlay controls */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        <button
                          onClick={() => handleToggle(banner)}
                          className="p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
                          title={banner.status === 'active' ? 'Hide' : 'Show'}
                          data-testid={`toggle-${banner.id}`}
                        >
                          {banner.status === 'active' ? <EyeOff className="w-4 h-4 text-slate-600" /> : <Eye className="w-4 h-4 text-emerald-600" />}
                        </button>
                        <button
                          onClick={() => handleDelete(banner.id)}
                          className="p-2 rounded-full bg-white/90 hover:bg-red-50 shadow-lg transition-all"
                          title="Delete"
                          data-testid={`delete-banner-${banner.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                      {/* Info bar */}
                      <div className="p-2 bg-white flex items-center justify-between">
                        <span className="text-[10px] text-slate-400 truncate max-w-[60%]">{banner.image_name}</span>
                        <Badge className={`text-[9px] ${banner.status === 'active' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                          {banner.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </DashboardLayout>
  );
}
